import { Suspense, lazy, useEffect, type ReactElement } from 'react';
import { useRoute, splitLocale, navigate, parseRoute, roomPath } from './router.js';
import { bindRoomStore, useRoomStore } from './store/roomStore.js';
import { leaveRoom } from './net/leaveRoom.js';
import { syncDocumentLanguage, useUiStore } from './store/uiStore.js';
import { HomeScreen } from './screens/HomeScreen.js';
import { isTranslated } from './content/index.js';
import { RoomScreen } from './screens/RoomScreen.js';
import { LegalScreen } from './screens/LegalScreen.js';

/**
 * Pages de contenu, chargees A LA DEMANDE.
 *
 * Elles pesent une douzaine de kilo-octets comprimes, soit un dixieme du
 * bundle, pour de la prose qu'un joueur venu jouer ne lira pas. Meme
 * arbitrage que pour les catalogues de langue : un fragment par page, telecharge
 * quand on y va. Les moteurs les voient quand meme, ils executent le
 * JavaScript.
 */
const HowToPlayScreen = lazy(async () => ({
  default: (await import('./screens/HowToPlayScreen.js')).HowToPlayScreen,
}));
const ModesScreen = lazy(async () => ({
  default: (await import('./screens/ModesScreen.js')).ModesScreen,
}));
const BlogScreen = lazy(async () => ({
  default: (await import('./screens/BlogScreen.js')).BlogScreen,
}));
const BlogPostScreen = lazy(async () => ({
  default: (await import('./screens/BlogScreen.js')).BlogPostScreen,
}));
import { BrowseScreen } from './screens/BrowseScreen.js';
import { AccountScreen } from './screens/AccountScreen.js';
import { PurchaseScreen } from './screens/PurchaseScreen.js';
import { AccountButton } from './ui/AccountButton.js';
import { ToastStack } from './ui/Toast.js';
import { MuteButton } from './ui/MuteButton.js';
import { ChatPanel } from './ui/ChatPanel.js';
import { AwayBanner } from './ui/AwayBanner.js';
import { ErrorBoundary } from './ui/ErrorBoundary.js';
import { ReconnectingBanner } from './ui/Screen.js';
import { useAudioUnlock, useGameSounds } from './audio/useGameSounds.js';
import { useRoundInterstitial } from './ads/useRoundInterstitial.js';
import { initAds } from './ads/adapter.js';
import { useConsentStore } from './consent/consentStore.js';
import { trackPageView } from './analytics.js';
import './styles/game.css';

/**
 * Les pages de dev n'existent qu'en dev : `import.meta.env.DEV` est remplace
 * par false au build, donc les branches entieres : et les imports dynamiques
 * avec elles : sont eliminees par Rollup.
 */
const DEV_PAGES = import.meta.env.DEV
  ? {
      sandbox: lazy(async () => import('./sandbox/SandboxPage.js')),
      zones: lazy(async () => import('./sandbox/ZoneEditorPage.js')),
      bot: lazy(async () => import('./sandbox/BotStudioPage.js')),
      rush: lazy(async () => import('./rush/RushPage.js')),
      stats: lazy(async () => import('./sandbox/StatsPage.js')),
    }
  : null;

// Une seule fois, au chargement du module : le store suit la connexion.
bindRoomStore();

export function App(): ReactElement {
  const route = useRoute();
  const room = useRoomStore((state) => state.room);
  const status = useRoomStore((state) => state.status);

  const consent = useConsentStore((state) => state.status);

  useAudioUnlock();
  useGameSounds();
  useRoundInterstitial();

  // La publicite ne demarre que sur un consentement explicite. Ce cablage
  // vivait dans le bandeau maison, retire depuis : le laisser mourir avec lui
  // aurait fait qu'une CMP posant `granted` n'aurait rien declenche du tout.
  // Aujourd'hui le statut reste `unknown`, donc rien ne s'initialise.
  useEffect(() => {
    void initAds(consent === 'granted');
  }, [consent]);

  // Une vue de page, au premier rendu. Le canal d'arrivee (?ref=itch) part
  // avec : c'est toute l'attribution, et elle ne coute rien.
  useEffect(trackPageView, []);

  /**
   * Une adresse prefixee (`/en`, `/de/modes`…) impose SA langue, une seule fois.
   *
   * Elle vient d'un resultat de recherche ou d'un lien : c'est une intention
   * aussi explicite qu'un clic dans le selecteur, et le HTML deja affiche
   * annonce cette langue-la. La laisser perdre contre une preference memorisee
   * afficherait autre chose que ce que le moteur a indexe, et autre chose que
   * ce que le titre de l'onglet promet.
   *
   * On ne nettoie PAS l'URL ensuite : la reecrire ferait perdre au partage et
   * au rechargement l'adresse qui portait la langue.
   */
  useEffect(() => {
    const { language, rest } = splitLocale(window.location.pathname);
    if (language === null) return;
    useUiStore.getState().setLanguage(language);

    // ADRESSE FANTOME. `/es/how-to-play` sert du francais tant que l'espagnol
    // n'est pas traduit : on ramene donc l'URL sur celle qui existe vraiment.
    // La langue reste celle demandee, seule l'adresse est corrigee. Sans cela
    // on partage, on met en favori et on fait explorer une adresse qui promet
    // une traduction absente.
    if (rest !== '/' && !isTranslated(language)) navigate(rest, { replace: true });
  }, []);

  // L'attribut lang du document suit la langue choisie : les lecteurs d'ecran
  // et la synthese vocale s'en servent pour choisir la prononciation.
  useEffect(syncDocumentLanguage, []);

  // Un avatar gratuit des la premiere visite : sans compte on n'en choisit pas,
  // mais on en a un, et il ne bouge plus.
  useEffect(() => useUiStore.getState().ensureAvatar(), []);

  // L'URL suit le salon : c'est elle qui permet de revenir apres fermeture.
  //
  // La dependance est le CODE, pas l'objet. Le store remplace `room` a chaque
  // instantane recu, et l'effet se rejouait donc a la moindre secousse de la
  // table : quelqu'un rejoint, un reglage bouge, et l'URL etait ramenee de
  // force sur le salon. Un joueur qui venait de cliquer sur RETOUR y etait
  // renvoye aussitot, sans rien pouvoir y faire.
  const roomCode = room?.code ?? null;
  useEffect(() => {
    if (roomCode === null) return;
    const expected = roomPath(roomCode);
    if (window.location.pathname === expected) return;

    // Entrer dans un salon EMPILE une entree : le salon est un endroit ou l'on
    // va, et RETOUR doit en ressortir. En remplacant, l'accueil disparaissait
    // de l'historique et RETOUR quittait le site.
    // On remplace dans un seul cas : on est deja sur une URL de salon, et il ne
    // s'agit que de la corriger (code en minuscules, par exemple). Empiler la
    // laisserait la forme fautive dans l'historique.
    const correcting = parseRoute(window.location.pathname).name === 'room';
    navigate(expected, { replace: correcting });
  }, [roomCode]);

  // RETOUR du navigateur depuis un salon : on quitte pour de bon.
  //
  // Sans cela on revenait a l'accueil en restant ASSIS a la table : la place
  // etait toujours prise, la partie attendait quelqu'un qui ne voyait plus
  // rien, et le premier instantane venu ramenait le joueur dans le salon. Le
  // depart passe par le meme chemin que le bouton « Quitter », il n'y a pas
  // deux facons de sortir.
  //
  // Seul `popstate` declenche ce depart, donc seul un geste DELIBERE du joueur.
  // Les navigations du jeu (entree dans un salon, redirections) passent par
  // pushState/replaceState, qui n'emettent pas cet evenement.
  useEffect(() => {
    const onPop = (): void => {
      const current = useRoomStore.getState().room;
      if (current === null) return;
      if (parseRoute(window.location.pathname).name === 'room') return;
      leaveRoom();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Une socket coupee en pleine partie ne bloque rien : la banniere informe et
  // la reconnexion se fait toute seule.
  const reconnecting = status === 'reconnecting' && room !== null;

  if (
    DEV_PAGES !== null &&
    (route.name === 'sandbox' ||
      route.name === 'zones' ||
      route.name === 'bot' ||
      route.name === 'rush' ||
      route.name === 'stats')
  ) {
    const Page = DEV_PAGES[route.name];
    return (
      <Suspense fallback={<p className="sandbox__status">…</p>}>
        <Page />
      </Suspense>
    );
  }

  return (
    <ErrorBoundary>
      {route.name === 'room' ? (
        <RoomScreen code={route.code} />
      ) : route.name === 'browse' ? (
        <BrowseScreen />
      ) : route.name === 'account' ? (
        <AccountScreen />
      ) : route.name === 'purchase' ? (
        <PurchaseScreen />
      ) : route.name === 'howToPlay' ? (
        <Suspense fallback={null}>
          <HowToPlayScreen />
        </Suspense>
      ) : route.name === 'modes' ? (
        <Suspense fallback={null}>
          <ModesScreen />
        </Suspense>
      ) : route.name === 'blog' ? (
        <Suspense fallback={null}>
          <BlogScreen />
        </Suspense>
      ) : route.name === 'article' ? (
        <Suspense fallback={null}>
          <BlogPostScreen slug={route.slug} />
        </Suspense>
      ) : route.name === 'legal' || route.name === 'privacy' || route.name === 'terms' ? (
        <LegalScreen kind={route.name} />
      ) : (
        <HomeScreen />
      )}

      {/* Le compte s'atteint la ou l'on a le temps : accueil, salon, arrivee
          dans une partie. Jamais pendant une manche, ou il volerait un coin de
          l'ecran a ce qui se joue. Le salon (`room`) le garde tant que la
          partie n'a pas commence : RoomScreen affiche alors le lobby. */}
      {(route.name === 'home' || route.name === 'room') &&
        (room === null || room.phase === 'LOBBY' || room.phase === 'GAME_END') && <AccountButton />}

      <MuteButton />
      {route.name === 'room' && <ChatPanel />}
      <AwayBanner />
      <ReconnectingBanner visible={reconnecting} />
      <ToastStack />
    </ErrorBoundary>
  );
}
